import type { FC } from "react"
import Avatar from "@mui/material/Avatar"
import Tooltip from "@mui/material/Tooltip"
import { useTheme } from "@mui/material"
import get from "lodash/get"

import type { EntityType } from "types/common.types"
import { ENTITY_VISUALS } from "helpers/config/entityVisuals"

interface EntityIconProps {
  entity: EntityType
  size?: number
  // Override the config color
  color?: string
  variant?: "bare" | "avatar"
  avatarSize?: number
  // Override the avatar background; falls back to the config's light color.
  bgColor?: string
  // When set, the icon is wrapped in a MUI Tooltip.
  tooltip?: string
}

const EntityIcon: FC<EntityIconProps> = ({
  entity,
  size = 18,
  color,
  variant = "bare",
  avatarSize = 24,
  bgColor,
  tooltip,
}) => {
  const theme = useTheme()
  const visual = ENTITY_VISUALS[entity]

  const resolvedColor = color ?? (get(theme.palette, visual.colorPath) as string)
  const icon = visual.renderIcon({ color: resolvedColor, size })

  const body =
    variant === "avatar" ? (
      <Avatar
        sx={{
          width: avatarSize,
          height: avatarSize,
          bgcolor: bgColor ?? visual.lightPath,
        }}
      >
        {icon}
      </Avatar>
    ) : (
      icon
    )

  if (tooltip) {
    // Wrap in a span so the Tooltip has a ref-forwarding anchor (bare custom
    // icons don't forward refs).
    return (
      <Tooltip title={tooltip} arrow>
        <span style={{ display: "inline-flex" }}>{body}</span>
      </Tooltip>
    )
  }

  return <>{body}</>
}

export default EntityIcon
